import { Component, DestroyRef, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';

import { COST_CENTER_PATTERN } from '@shared-constants/pattern-constants';
import { IHttpResponseCostCenter } from '@shared-interfaces/api';

import { ContractHandler } from '../../scripts/contracts/contract-handler';
import { ApiService } from '../../services/api.service';
import { DatahubService } from '../../services/datahub.service';

@Component({
  selector: 'contract-select',
  templateUrl: './contract-select.component.html',
  styleUrl: './contract-select.component.css',
  standalone: false,
})
export class ContractSelectComponent implements OnInit {
  @Input() shouldAllowSearching = true;
  @Input() hasEmbeddedComponent = false;
  @Input() shouldDisableWeDate = false;
  @Input() contracts: ContractHandler[] = [];
  @Input() barsClass = '';
  @Input() headerText = 'Select a Contract';
  @Input() updateCostCenterNotifier$?: Observable<number>;

  @Output() public readonly next = new EventEmitter();
  @Output() public readonly backClicked = new EventEmitter();

  public handler = this.datahub.weDate;
  public readonly costCenterFormCtrl = new FormControl<number>(Number(this.datahub.user.approvingCostCenter), [
    Validators.required,
    Validators.pattern(COST_CENTER_PATTERN),
  ]);
  public isSearching = false;
  public shouldHaveIncrementors = true;
  public retrievedCostCenterInfo?: IHttpResponseCostCenter;
  public isRetrievingCostCenterName = false;

  private serviceOrder = '';
  private contractEntity: ContractHandler;

  constructor(
    private readonly datahub: DatahubService,
    private readonly router: Router,
    private readonly destroyRef: DestroyRef,
    private readonly apiService: ApiService,
  ) {}

  ngOnInit(): void {
    this.costCenterFormCtrl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        if (this.costCenterFormCtrl.valid) {
          this.costCenterFormCtrl.markAllAsTouched(); // Trigger validation
          this.fetchCostCenterWhenValid();
          this.fetchContracts();
        }
      },
      error: (err) => {
        console.error('Error during cost center form value change:', err);
      },
    });

    if (this.updateCostCenterNotifier$ != null) {
      this.updateCostCenterNotifier$.subscribe({
        next: (costCenterNumber) => {
          this.costCenterFormCtrl.setValue(costCenterNumber);
        },
        error: (err) => {
          console.error('Error updating cost center:', err);
        },
      });
    }

    if (this.shouldDisableWeDate) this.shouldHaveIncrementors = false;
    if (this.shouldAllowSearching) this.handleDateChange();
    if (this.shouldAllowSearching) this.fetchContracts();
  }

  public select(contract: ContractHandler) {
    this.serviceOrder = contract.getServiceOrder();
    this.contractEntity = contract;
    this.navToTimesheet();
  }

  public fetchCostCenterWhenValid(): void {
    this.isRetrievingCostCenterName = true;
    this.retrievedCostCenterInfo = undefined;
    this.apiService.getCostCenter$(this.costCenterFormCtrl.value?.toString()).subscribe({
      next: (cc) => {
        this.isRetrievingCostCenterName = false;
        if (cc == null) {
          this.costCenterFormCtrl.setErrors({ costCenterDoesNotExist: true });
        } else {
          this.retrievedCostCenterInfo = cc;
        }
      },
      error: (err) => {
        this.isRetrievingCostCenterName = false;
        console.error('Error retrieving cost center:', err);
      },
    });
  }

  private navToTimesheet() {
    if (this.serviceOrder === '') {
      this.datahub.messageService.error('No service order exists on this contract! Please contact Davey Time');
      return;
    }
    if (this.hasEmbeddedComponent) {
      this.next.emit(this.contractEntity);
      return;
    }
    void this.router.navigateByUrl(
      `/billing/review?wedate=${this.handler.getSapWeDateString()}&order=${this.serviceOrder}&approver=${this.datahub.user.approvingId}`,
    );
  }

  public handleDateChange() {
    this.fetchContracts();
    this.datahub.updateQueryParams();
  }

  public resetCostCenterValidationIfEmpty(): void {
    // When no cost center and input is blurred, resets validation
    if (this.costCenterFormCtrl.value == null) {
      this.costCenterFormCtrl.reset();
      this.retrievedCostCenterInfo = undefined;
    }
  }

  private fetchContracts() {
    this.isSearching = true;
    this.datahub.contracts.complexSearchByCostCenter$(this.costCenterFormCtrl.value?.toString(), this.datahub.weDate).subscribe({
      next: (contracts: ContractHandler[]) => {
        this.contracts = contracts;
        this.isSearching = false;
      },
      error: (err: unknown) => {
        this.isSearching = false;
        console.error('Something went wrong when searching for contracts:', err);
      },
    });
  }
}
